import { emailRegex, validationTranslationKeys } from '@va/constants';
import { useAddNotification } from '@va/dashboard/util-hooks';
import { InformationIcon, Spinner } from '@va/icons';
import { useTranslate } from '@va/localization';
import { CustomDomainPlatformType, CustomDomainsApi, SenderEmail } from '@va/shared/api/custom-domains';
import { ActiveDomainItem, InactiveDomainItem, PendingDomainItem } from '@va/shared/custom-domains';
import { useConfirmationDialogContext } from '@va/shared/util-confirmation';
import { selectAgencyId, selectAgencyUIId } from '@va/standalone/shared/selectors';
import { SubSectionHeader } from '@va/ui/components/white-label-customisation';
import { Button, TextInput } from '@va/ui/design-system';
import { capitalizeFirstLetter } from '@va/util/helpers';
import { AxiosError } from 'axios';
import { useFormik } from 'formik';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { object, string } from 'yup';
import { SendTestEmailSection } from '../SendTestEmailSection';
import { SenderEmailChangeModal } from './SenderEmailChangeModal';
import { useSenderEmailActions } from './useSenderEmailActions';

export const CustomSenderEmail = () => {
  const [isLoadingEmailCreation, setIsLoadingEmailCreation] = useState(false);

  const translate = useTranslate();

  const agencyId = useSelector(selectAgencyId);
  const agencyUiId = useSelector(selectAgencyUIId);

  const { showErrorNotification } = useAddNotification();

  const {
    activeSenderEmail,
    activeSenderEmailActions,
    inactiveSenderEmailActions,
    inactiveSenderEmails,
    pendingSenderEmail,
    pendingSenderEmailActions,
  } = useSenderEmailActions({ companyId: agencyId ?? '' });

  const listSenderEmailsReq = CustomDomainsApi.useListSenderEmails({ companyId: agencyId ?? '' });

  const createEmailSender = CustomDomainsApi.useCreateSenderEmail();

  const { handleChange, values, errors, setFieldValue, handleBlur, touched, dirty, submitForm, resetForm } = useFormik({
    initialValues: {
      emailAddress: '',
    },
    onSubmit: async ({ emailAddress }) => {
      if (!agencyId) return;
      try {
        setIsLoadingEmailCreation(true);
        const res = await CustomDomainsApi.createSenderEmail({
          companyId: agencyId,
          email: emailAddress,
          type: CustomDomainPlatformType.AWL,
          senderName: '',
          skipActivation: true,
        });
        await listSenderEmailsReq.mutate();
        resetForm();

        confirm(
          () => {},
          ({ onClose }) => (
            <SenderEmailChangeModal
              customSenderEmailId={res.payload.senderEmail.id}
              email={emailAddress}
              isOpen={true}
              closeModal={onClose}
            />
          ),
        );
      } catch (error) {
        if (error instanceof AxiosError) {
          const msg = error.response?.data?.detail;
          showErrorNotification(undefined, capitalizeFirstLetter(msg));
        } else {
          showErrorNotification();
        }
      } finally {
        setIsLoadingEmailCreation(false);
      }
    },
    enableReinitialize: true,
    validateOnChange: true,
    validationSchema: object({
      emailAddress: string()
        .matches(emailRegex, translate(validationTranslationKeys.invalidEmail))
        .required(translate(validationTranslationKeys.required)),
    }),
  });

  const { confirm } = useConfirmationDialogContext();

  const isSaveButtonDisabled = useMemo(() => {
    return (
      isLoadingEmailCreation ||
      !agencyUiId ||
      !dirty ||
      createEmailSender.isLoading ||
      pendingSenderEmail !== null ||
      !!errors.emailAddress
    );
  }, [agencyUiId, createEmailSender.isLoading, dirty, errors.emailAddress, isLoadingEmailCreation, pendingSenderEmail]);

  const saveButtonTooltip = useMemo(() => {
    if (!agencyUiId) return translate('whiteLabelCustomization.info.firstFillCustomUrl');
    if (!dirty) return translate('labels.noChanges');
    if (!isSaveButtonDisabled) {
      return (
        <div className='mt-3'>
          <InformationIcon className='inline' /> {translate('standalone.customDomain.saveButtonTooltip')}
        </div>
      );
    }

    if (pendingSenderEmail !== null) {
      return translate('customDomain.errors.alreadyExistingPendingSenderEmail');
    }
  }, [agencyUiId, dirty, isSaveButtonDisabled, pendingSenderEmail, translate]);

  const pendingSenderEmailPoolingInterval = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (!pendingSenderEmail) return;

    const interval = setInterval(() => {
      listSenderEmailsReq.mutate();
    }, 3000);
    pendingSenderEmailPoolingInterval.current = interval;

    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingSenderEmail]);

  return (
    <div>
      <SubSectionHeader
        text={translate('customSenderEmail.sectionTitle')}
        tooltip={translate('customSenderEmail.sectionTooltip')}
      />
      <div className='space-y-3'>
        <div className='space-y-2'>
          {activeSenderEmail && (
            <ActiveDomainItem
              value={formatEmailWithName(activeSenderEmail)}
              label={translate('customSenderEmail.labels.customEmailAddress')}
              actions={activeSenderEmailActions(activeSenderEmail)}
            />
          )}

          {pendingSenderEmail && (
            <PendingDomainItem
              value={formatEmailWithName(pendingSenderEmail)}
              label={translate('customSenderEmail.labels.customEmailAddress')}
              actions={pendingSenderEmailActions(pendingSenderEmail)}
            />
          )}

          {inactiveSenderEmails &&
            inactiveSenderEmails.map((item) => (
              <InactiveDomainItem
                key={item.id}
                status={item.status}
                value={formatEmailWithName(item)}
                label={translate('customSenderEmail.labels.customEmailAddress')}
                actions={inactiveSenderEmailActions(item)}
              />
            ))}
        </div>

        <TextInput
          wrapperClassName='overflow-hidden grow'
          label={translate('customDomain.customEmailAddress.input.label')}
          placeholder={translate('customDomain.customEmailAddress.input.placeholder')}
          name='emailAddress'
          value={values.emailAddress}
          clearField={() => setFieldValue('emailAddress', '')}
          onChange={handleChange}
          onBlur={handleBlur}
          required
          error={(touched.emailAddress && errors.emailAddress) || undefined}
          showErrorMessage={true}
          bgColor='#F0F0F0'
          bgColorHover='#F9F9F9'
        />
      </div>
      <Button
        tooltip={saveButtonTooltip}
        tooltipProps={{ maxWidth: 'max-w-[25rem]' }}
        disabled={isSaveButtonDisabled}
        onClick={() => {
          submitForm();
        }}
        icon={isLoadingEmailCreation ? () => <Spinner /> : undefined}
        className='w-full mt-3 mb-5 justify-center'
        text={translate('customSenderEmail.saveChanges')}
      />
      <SendTestEmailSection activeSender={activeSenderEmail} />
    </div>
  );
};

function formatEmailWithName(s: SenderEmail) {
  if (!s.name) return s.email;
  return `${s.email}  [ ${s.name} ]`;
}
